import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { LogIn, ShieldCheck, Info } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) nav(from, { replace: true });
    else setError(r.error);
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img
          src="/media/research-lab.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-unisic-blue/80" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Logo variant="light" />
          <div>
            <div className="eyebrow" style={{ color: "#E5C158" }}>Espace institutionnel</div>
            <h2 className="mt-3 text-3xl font-bold leading-tight">
              Portail réservé aux personnels du Secrétariat Général à la Recherche
            </h2>
            <p className="mt-4 text-white/80 text-sm max-w-md leading-relaxed">
              L'accès au tableau de bord est réservé aux comptes créés préalablement par
              l'administrateur. Il n'existe pas d'ouverture de compte publique.
            </p>
            <div className="mt-8 flex items-center gap-2 text-xs uppercase tracking-widest text-unisic-gold">
              <ShieldCheck className="w-4 h-4" /> Authentification institutionnelle
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="eyebrow eyebrow-dark">Connexion</div>
          <h1 className="mt-2 text-3xl font-bold text-unisic-blue">Accéder au tableau de bord</h1>
          <p className="mt-2 text-sm text-slate-600">
            Utilisez votre adresse e-mail institutionnelle et le mot de passe fourni par
            l'administrateur.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                Adresse e-mail
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-4 py-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-unisic-gold text-sm"
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                Mot de passe
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border border-slate-300 px-4 py-3 rounded-sm focus:outline-none focus:ring-2 focus:ring-unisic-gold text-sm"
                data-testid="input-password"
              />
            </div>

            {error && (
              <div
                className="text-sm text-unisic-red bg-red-50 border border-red-100 px-3 py-2 rounded-sm"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
              data-testid="btn-submit"
            >
              <LogIn className="w-4 h-4" />
              {loading ? "Connexion en cours..." : "Se connecter"}
            </button>

            <div className="text-right text-xs">
              <Link
                to="/mot-de-passe-oublie"
                className="text-slate-600 hover:text-unisic-blue underline-offset-4 hover:underline"
                data-testid="link-forgot-password"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

          <div className="mt-8 paper-card p-4 flex gap-3 items-start bg-slate-50/50">
            <Info className="w-4 h-4 text-unisic-gold flex-shrink-0 mt-0.5" />
            <div className="text-xs text-slate-600 leading-relaxed">
              <span className="font-semibold text-unisic-blue">Pas de compte ?</span> Les comptes
              sont créés par le Secrétariat Général à la Recherche. Contactez{" "}
              <a href="mailto:sgr@unisic-recherche.net" className="link-underline">
                sgr@unisic-recherche.net
              </a>{" "}
              pour obtenir un accès.
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-slate-200 text-xs text-slate-500">
            <Link to="/" className="hover:text-unisic-blue">← Retour au portail public</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
